import { LICENSE_DURATIONS_BY_TYPE, type BranchType, type CreatePharmacyInput, type LicenseDurationKey, type PlannedLicenseType } from '@quds/shared';
import type { ProvisionPharmacyInput } from '@/data';
import type { FieldIssue } from '@/lib/api';
import { addDays, addMonths, toDateInputValue } from '@/lib/format';
import { DURATION_LABELS } from '../components/labels';
import { EMPTY_PHARMACY_INFO, mapFieldIssues, toInfoInput, validatePharmacyInfo, type FieldErrors, type PharmacyInfoValues } from '../validation';

export type DurationKey = LicenseDurationKey;

const DURATION_SPAN: Record<DurationKey, { months?: number; days?: number }> = {
  m1: { months: 1 },
  m3: { months: 3 },
  m6: { months: 6 },
  m12: { months: 12 },
  m24: { months: 24 },
  m36: { months: 36 },
  d14: { days: 14 },
  d30: { days: 30 },
};

export const DURATIONS_BY_TYPE = LICENSE_DURATIONS_BY_TYPE;
export { BRANCH_TYPE_LABELS } from '../components/labels';

/**
 * Wizard state. "services" and "license" record what was agreed during
 * onboarding (requested / planned); nothing is provisioned in production.
 */
export interface WizardState {
  info: PharmacyInfoValues;
  branch: { name: string; reference: string; type: BranchType };
  services: { cloud: boolean; api: boolean; ownerMobile: boolean };
  license: { type: PlannedLicenseType; duration: DurationKey | ''; expiresAt: string };
  /**
   * Batch A — when `createProductionEnvironment` is on, creating the pharmacy
   * also provisions a real Production tenant + first branch (+ optional API
   * key / owner). Defaults ON for operational safety during onboarding.
   */
  production: {
    createProductionEnvironment: boolean;
    createApiKey: boolean;
    owner: { enabled: boolean; username: string; fullName: string };
  };
}

export function computeExpiry(duration: DurationKey | '', from = new Date()): string {
  if (!duration) return '';
  const d = DURATION_SPAN[duration];
  const date = d.months ? addMonths(from, d.months) : addDays(from, d.days ?? 0);
  return toDateInputValue(date);
}

export function initialWizardState(): WizardState {
  return {
    info: { ...EMPTY_PHARMACY_INFO },
    branch: { name: 'Main Branch', reference: '', type: 'main' },
    services: { cloud: true, api: false, ownerMobile: true },
    license: { type: 'annual', duration: 'm12', expiresAt: computeExpiry('m12') },
    production: { createProductionEnvironment: true, createApiKey: false, owner: { enabled: false, username: '', fullName: '' } },
  };
}

export type BranchErrors = FieldErrors<WizardState['branch']>;
export type LicenseErrors = FieldErrors<WizardState['license']>;

const REFERENCE = /^[A-Z0-9][A-Z0-9-]{1,15}$/;

export function validateBranch(b: WizardState['branch']): BranchErrors {
  const e: BranchErrors = {};
  if (!b.name.trim()) e.name = 'Enter the branch name.';
  if (!b.reference.trim()) e.reference = 'Enter a branch reference, e.g. RS-01.';
  else if (!REFERENCE.test(b.reference.trim().toUpperCase())) e.reference = 'Use 2–16 letters, numbers or dashes, e.g. RS-01.';
  return e;
}

export function validateLicense(l: WizardState['license']): LicenseErrors {
  const e: LicenseErrors = {};
  if (l.type !== 'perpetual') {
    if (!l.duration) e.duration = 'Select a license duration.';
    if (!l.expiresAt) e.expiresAt = 'Select an expiration date.';
    else if (l.expiresAt <= toDateInputValue(new Date())) e.expiresAt = 'Expiration date must be in the future.';
  }
  return e;
}

export { validatePharmacyInfo };

export function durationLabel(state: WizardState): string {
  if (state.license.type === 'perpetual') return 'No expiration';
  return state.license.duration ? DURATION_LABELS[state.license.duration] : '—';
}

export function toCreateInput(state: WizardState): CreatePharmacyInput {
  const perpetual = state.license.type === 'perpetual';
  return {
    ...toInfoInput(state.info),
    branch: { name: state.branch.name.trim(), referenceCode: state.branch.reference.trim().toUpperCase(), type: state.branch.type },
    requestedServices: state.services,
    deviceSetupMode: 'connect_later',
    plannedLicense: {
      type: state.license.type,
      duration: perpetual || !state.license.duration ? null : state.license.duration,
      expiresOn: perpetual || !state.license.expiresAt ? null : state.license.expiresAt,
    },
  };
}

/** Batch A — maps wizard state to the combined provision-business payload. */
export function toProvisionInput(state: WizardState): ProvisionPharmacyInput {
  const owner = state.production.owner;
  return {
    ...toCreateInput(state),
    createProductionEnvironment: state.production.createProductionEnvironment,
    production: {
      createApiKeyForFirstBranch: state.production.createApiKey,
      owner: owner.enabled && owner.username.trim() && owner.fullName.trim() ? { username: owner.username.trim(), fullName: owner.fullName.trim() } : undefined,
    },
  };
}

/** Routes API validation issues back to the wizard step that owns the field. */
export function mapServerIssues(issues: FieldIssue[]): { step: number; info: FieldErrors<PharmacyInfoValues>; branch: BranchErrors; license: LicenseErrors } {
  const info = mapFieldIssues<PharmacyInfoValues>(issues.filter((i) => !i.path.includes('.')), {});
  const branch = mapFieldIssues<WizardState['branch']>(
    issues.filter((i) => i.path.startsWith('branch.')),
    { 'branch.name': 'name', 'branch.referenceCode': 'reference', 'branch.type': 'type' },
  );
  const license = mapFieldIssues<WizardState['license']>(
    issues.filter((i) => i.path.startsWith('plannedLicense.')),
    { 'plannedLicense.type': 'type', 'plannedLicense.duration': 'duration', 'plannedLicense.expiresOn': 'expiresAt' },
  );
  const step = Object.keys(info).length ? 0 : Object.keys(branch).length ? 1 : Object.keys(license).length ? 3 : 4;
  return { step, info, branch, license };
}
